class ElProductInformation extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;
    const dataNode = this.querySelector("[data-el-product-data]");
    if (!dataNode) return;
    try {
      this.productData = JSON.parse(dataNode.textContent);
    } catch (error) {
      return;
    }
    this.variantMap = new Map(
      (this.productData.variants || []).map((variant) => [
        Number(variant.id),
        variant,
      ]),
    );
    this.abortController = new AbortController();
    this.addEventListener("click", this.handleClick.bind(this));
    this.addEventListener("keydown", this.handleKeydown.bind(this));
    document.addEventListener("click", this.handleDocumentClick.bind(this), {
      signal: this.abortController.signal,
    });
    this.initialize();
  }
  disconnectedCallback() {
    this.abortController?.abort();
  }
  initialize() {
    this.querySelectorAll(
      '[data-el-purchase-option][data-option-type="subscription"]',
    ).forEach((option) => this.syncSellingPlans(option));
    this.querySelectorAll("[data-el-purchase-option]").forEach((option) =>
      this.updateOption(option),
    );
    const preferred =
      this.querySelector(
        "[data-el-purchase-option].is-active:not(.is-unavailable)",
      ) || this.querySelector("[data-el-purchase-option]:not(.is-unavailable)");
    if (preferred) this.activateOption(preferred);
    this.updateDeliveryDates();
    this.updateAtc();
  }
  handleClick(event) {
    const variantOption = event.target.closest("[data-el-variant-option]");
    if (variantOption && this.contains(variantOption)) {
      if (variantOption.disabled) return;
      const picker = variantOption.closest("[data-el-variant-select]");
      this.selectVariant(picker, variantOption);
      return;
    }
    const variantTrigger = event.target.closest("[data-el-variant-trigger]");
    if (variantTrigger && this.contains(variantTrigger)) {
      const purchaseOption = variantTrigger.closest(
        "[data-el-purchase-option]",
      );
      if (purchaseOption && !purchaseOption.classList.contains("is-active"))
        this.activateOption(purchaseOption);
      this.toggleVariantPicker(
        variantTrigger.closest("[data-el-variant-select]"),
      );
      return;
    }
    const sellingPlan = event.target.closest("[data-el-selling-plan]");
    if (sellingPlan && this.contains(sellingPlan)) {
      if (sellingPlan.disabled) return;
      const purchaseOption = sellingPlan.closest("[data-el-purchase-option]");
      this.activateOption(purchaseOption);
      purchaseOption
        .querySelectorAll("[data-el-selling-plan]")
        .forEach((button) => {
          const active = button === sellingPlan;
          button.classList.toggle("is-active", active);
          button.setAttribute("aria-pressed", active ? "true" : "false");
        });
      this.updateOption(purchaseOption);
      this.updateAtc();
      return;
    }
    const header = event.target.closest("[data-el-option-header]");
    if (header && this.contains(header)) {
      const purchaseOption = header.closest("[data-el-purchase-option]");
      if (!purchaseOption.classList.contains("is-unavailable"))
        this.activateOption(purchaseOption);
      return;
    }
    const atc = event.target.closest("[data-el-atc]");
    if (atc && this.contains(atc)) {
      this.addSelection(atc, false);
      return;
    }
    const buyNow = event.target.closest("[data-el-buy-now]");
    if (buyNow && this.contains(buyNow)) this.addSelection(buyNow, true);
  }
  handleKeydown(event) {
    if (event.key === "Escape") {
      this.closeVariantPickers();
      return;
    }
    const header = event.target.closest("[data-el-option-header]");
    if (header && (event.key === "Enter" || event.key === " ")) {
      event.preventDefault();
      const purchaseOption = header.closest("[data-el-purchase-option]");
      if (!purchaseOption.classList.contains("is-unavailable"))
        this.activateOption(purchaseOption);
    }
  }
  handleDocumentClick(event) {
    if (!event.target.closest(".el-product-variant-picker"))
      this.closeVariantPickers();
  }
  activateOption(option) {
    if (!option || option.classList.contains("is-unavailable")) return;
    option
      .closest(".el-product-purchase-options")
      ?.querySelectorAll("[data-el-purchase-option]")
      .forEach((item) => {
        const active = item === option;
        item.classList.toggle("is-active", active);
        item
          .querySelector("[data-el-option-header]")
          ?.setAttribute("aria-checked", active ? "true" : "false");
      });
    if (option.dataset.optionType === "subscription")
      this.syncSellingPlans(option);
    this.updateOption(option);
    this.updateAtc();
  }
  selectVariant(picker, variantOption) {
    const variantId = Number(variantOption.dataset.variantId);
    picker.dataset.variantId = String(variantId);
    picker.dataset.variantColor = String(variantOption.dataset.variantColor);
    picker.dataset.pickerId = String(variantOption.dataset.pickerId);
    document.getElementById(picker.dataset.pickerId + "-index").style.background = variantOption.dataset.variantColor || "#000000";
    picker
      .querySelector("[data-el-variant-label]")
      ?.replaceChildren(
        document.createTextNode(
          variantOption.dataset.variantTitle ||
            variantOption.textContent.trim(),
        ),
      );
    const colorSwatch = picker.querySelector("[data-el-variant-color]");
    if (colorSwatch) {
      colorSwatch.style.setProperty(
        "--bg-color",
        variantOption.dataset.variantColor || "",
      );
    }
    picker.querySelectorAll("[data-el-variant-option]").forEach((item) => {
      const selected = item === variantOption;
      item.classList.toggle("is-selected", selected);
      item.setAttribute("aria-selected", selected ? "true" : "false");
    });
    this.closeVariantPicker(picker);
    const purchaseOption = picker.closest("[data-el-purchase-option]");
    if (purchaseOption.dataset.optionType === "subscription")
      this.syncSellingPlans(purchaseOption);
    this.updateOption(purchaseOption);
    if (purchaseOption.classList.contains("is-active")) this.updateAtc();
  }
  toggleVariantPicker(picker) {
    const open = picker.classList.contains("is-open");
    this.closeVariantPickers();
    if (!open) {
      picker.classList.add("is-open");
      picker
        .querySelector("[data-el-variant-trigger]")
        ?.setAttribute("aria-expanded", "true");
    }
  }
  closeVariantPicker(picker) {
    picker?.classList.remove("is-open");
    picker
      ?.querySelector("[data-el-variant-trigger]")
      ?.setAttribute("aria-expanded", "false");
  }
  closeVariantPickers() {
    this.querySelectorAll("[data-el-variant-select].is-open").forEach(
      (picker) => this.closeVariantPicker(picker),
    );
  }
  getVariantIds(option) {
    return Array.from(option.querySelectorAll("[data-el-variant-select]"))
      .map((picker) => Number(picker.dataset.variantId))
      .filter(Boolean);
  }
  syncSellingPlans(option) {
    const variantIds = this.getVariantIds(option);
    const buttons = Array.from(
      option.querySelectorAll("[data-el-selling-plan]"),
    );
    buttons.forEach((button) => {
      const planId = Number(button.dataset.planId);
      const available =
        variantIds.length > 0 &&
        variantIds.every((variantId) => {
          const variant = this.variantMap.get(variantId);
          return (
            variant?.available &&
            variant.sellingPlans?.some(
              (allocation) => Number(allocation.id) === planId,
            )
          );
        });
      button.disabled = !available;
      if (!available) {
        button.classList.remove("is-active");
        button.setAttribute("aria-pressed", "false");
      }
    });
    let selected = buttons.find(
      (button) => button.classList.contains("is-active") && !button.disabled,
    );
    if (!selected) selected = buttons.find((button) => !button.disabled);
    if (selected) {
      buttons.forEach((button) => {
        const active = button === selected;
        button.classList.toggle("is-active", active);
        button.setAttribute("aria-pressed", active ? "true" : "false");
      });
      option.classList.remove("is-unavailable");
    } else {
      option.classList.add("is-unavailable");
    }
  }
  getPricing(option) {
    const variantIds = this.getVariantIds(option);
    if (!variantIds.length) return { valid: false, total: 0, compare: 0 };
    const variants = variantIds.map((id) => this.variantMap.get(id));
    if (variants.some((variant) => !variant || !variant.available))
      return { valid: false, total: 0, compare: 0 };
    if (option.dataset.optionType === "subscription") {
      const planButton = option.querySelector(
        "[data-el-selling-plan].is-active:not(:disabled)",
      );
      if (!planButton) return { valid: false, total: 0, compare: 0 };
      const planId = Number(planButton.dataset.planId);
      let total = 0;
      let compare = 0;
      for (const variant of variants) {
        const allocation = variant.sellingPlans?.find(
          (item) => Number(item.id) === planId,
        );
        if (!allocation) return { valid: false, total: 0, compare: 0 };
        total += Number(allocation.price || 0);
        compare += Number(allocation.compareAtPrice || variant.price || 0);
      }
      return {
        valid: true,
        total,
        compare: compare > total ? compare : 0,
        planId,
      };
    }
    const base = variants.reduce(
      (sum, variant) => sum + Number(variant.price || 0),
      0,
    );
    const compareBase = variants.reduce(
      (sum, variant) =>
        sum +
        Math.max(
          Number(variant.compareAtPrice || 0),
          Number(variant.price || 0),
        ),
      0,
    );
    const discount = Math.min(
      100,
      Math.max(0, Number(option.dataset.discount || 0)),
    );
    const total = Math.round(base * ((100 - discount) / 100));
    const compare = discount > 0 ? base : compareBase;
    return { valid: true, total, compare: compare > total ? compare : 0 };
  }
  updateOption(option) {
    if (!option) return;
    if (option.dataset.optionType === "subscription")
      this.syncSellingPlans(option);
    const pricing = this.getPricing(option);
    option.classList.toggle("is-unavailable", !pricing.valid);
    const price = option.querySelector("[data-el-option-price]");
    const compare = option.querySelector("[data-el-option-compare]");
    if (price) price.textContent = this.formatMoney(pricing.total);
    if (compare) {
      compare.textContent = pricing.compare
        ? this.formatMoney(pricing.compare)
        : "";
      compare.classList.toggle("is-hidden", !pricing.compare);
    }
  }
  updateAcceleratedCheckout() {
    const active = this.querySelector("[data-el-purchase-option].is-active");

    const form = this.querySelector(".el-product-atc__accelerated-form");

    const variantInput = this.querySelector("[data-el-accelerated-variant]");

    const quantityInput = this.querySelector("[data-el-accelerated-quantity]");

    const sellingPlanInput = this.querySelector(
      "[data-el-accelerated-selling-plan]",
    );

    if (!active || !form || !variantInput) return;

    const items = this.collectItems();

    if (!items.length) return;

    const primaryItem = items[0];
    const additionalItems = items.slice(1);

    variantInput.value = String(primaryItem.id);

    if (quantityInput) {
      quantityInput.value = String(primaryItem.quantity || 1);
    }

    if (sellingPlanInput) {
      if (primaryItem.selling_plan) {
        sellingPlanInput.disabled = false;
        sellingPlanInput.value = String(primaryItem.selling_plan);
      } else {
        sellingPlanInput.disabled = true;
        sellingPlanInput.value = "";
      }
    }

    const idInputs = Array.from(
      form.querySelectorAll("[data-el-accelerated-item-id]"),
    );

    idInputs.forEach((idInput) => {
      const index = idInput.dataset.elAcceleratedItemId;

      const quantity = form.querySelector(
        `[data-el-accelerated-item-quantity="${index}"]`,
      );

      const plan = form.querySelector(
        `[data-el-accelerated-item-plan="${index}"]`,
      );

      idInput.disabled = true;
      idInput.value = "";

      if (quantity) {
        quantity.disabled = true;
        quantity.value = "1";
      }

      if (plan) {
        plan.disabled = true;
        plan.value = "";
      }
    });

    additionalItems.forEach((item, itemIndex) => {
      const index = itemIndex + 1;

      const idInput = form.querySelector(
        `[data-el-accelerated-item-id="${index}"]`,
      );

      const quantityInputAdditional = form.querySelector(
        `[data-el-accelerated-item-quantity="${index}"]`,
      );

      const planInput = form.querySelector(
        `[data-el-accelerated-item-plan="${index}"]`,
      );

      if (!idInput) return;

      idInput.value = String(item.id);
      idInput.disabled = false;

      if (quantityInputAdditional) {
        quantityInputAdditional.value = String(item.quantity || 1);
        quantityInputAdditional.disabled = false;
      }

      if (planInput) {
        if (item.selling_plan) {
          planInput.value = String(item.selling_plan);
          planInput.disabled = false;
        } else {
          planInput.value = "";
          planInput.disabled = true;
        }
      }
    });

    variantInput.dispatchEvent(
      new Event("input", {
        bubbles: true,
      }),
    );

    variantInput.dispatchEvent(
      new Event("change", {
        bubbles: true,
      }),
    );

    if (quantityInput) {
      quantityInput.dispatchEvent(
        new Event("input", {
          bubbles: true,
        }),
      );

      quantityInput.dispatchEvent(
        new Event("change", {
          bubbles: true,
        }),
      );
    }

    if (sellingPlanInput) {
      sellingPlanInput.dispatchEvent(
        new Event("input", {
          bubbles: true,
        }),
      );

      sellingPlanInput.dispatchEvent(
        new Event("change", {
          bubbles: true,
        }),
      );
    }

    idInputs.forEach((input) => {
      input.dispatchEvent(
        new Event("change", {
          bubbles: true,
        }),
      );
    });

    form.dispatchEvent(
      new Event("input", {
        bubbles: true,
      }),
    );

    form.dispatchEvent(
      new Event("change", {
        bubbles: true,
      }),
    );
  }
  updateAtc() {
    const active = this.querySelector("[data-el-purchase-option].is-active");

    const pricing = active
      ? this.getPricing(active)
      : {
          valid: false,
          total: 0,
          compare: 0,
        };

    this.querySelectorAll("[data-el-atc-price]").forEach((item) => {
      item.textContent = this.formatMoney(pricing.total);
    });

    this.querySelectorAll("[data-el-atc-compare]").forEach((item) => {
      item.textContent = pricing.compare
        ? this.formatMoney(pricing.compare)
        : "";

      item.classList.toggle("is-hidden", !pricing.compare);
    });

    this.querySelectorAll("[data-el-atc],[data-el-buy-now]").forEach(
      (button) => {
        button.disabled = !pricing.valid;
      },
    );

    this.updateAcceleratedCheckout();
  }
  collectItems() {
    const active = this.querySelector("[data-el-purchase-option].is-active");
    if (!active) return [];
    const pricing = this.getPricing(active);
    if (!pricing.valid) return [];
    const planId =
      active.dataset.optionType === "subscription" ? pricing.planId : null;
    const grouped = new Map();
    this.getVariantIds(active).forEach((variantId) => {
      const key = `product:${variantId}:${planId || 0}`;
      const existing = grouped.get(key);
      if (existing) {
        existing.quantity += 1;
      } else {
        const item = { id: variantId, quantity: 1 };
        if (planId) item.selling_plan = planId;
        grouped.set(key, item);
      }
    });
    if (active.dataset.optionType === "subscription") {
      active
        .querySelectorAll("[data-el-gift-product][data-gift-variant-id]")
        .forEach((gift) => {
          if (gift.dataset.giftAvailable === "false") return;
          const variantId = Number(gift.dataset.giftVariantId);
          if (!variantId) return;
          const blockId = gift.dataset.giftBlockId || String(variantId);
          const key = `gift:${variantId}:${blockId}`;
          grouped.set(key, {
            id: variantId,
            quantity: 1,
            properties: {
              _el_free_gift: "true",
              _el_gift_block: blockId,
            },
          });
        });
    }
    return Array.from(grouped.values());
  }
  async addSelection(button, redirectToCheckout) {
    if (button.disabled || button.classList.contains("is-loading")) return;

    const items = this.collectItems();

    if (!items.length) return;

    const atcLabel = button.querySelector("[data-el-atc-label]");
    const originalLabel = atcLabel ? atcLabel.textContent : button.textContent;

    button.classList.add("is-loading");
    button.disabled = true;

    if (atcLabel) {
      atcLabel.textContent = "Adding...";
    } else {
      button.textContent = "Adding...";
    }

    try {
      const root = window.Shopify?.routes?.root || "/";

      const response = await fetch(`${root}cart/add.js`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-Requested-With": "XMLHttpRequest",
        },
        body: JSON.stringify({
          items,
          sections: "cart-drawer,cart-icon-bubble",
          sections_url: window.location.pathname + window.location.search,
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(
          result?.description ||
            result?.message ||
            "Unable to add items to cart.",
        );
      }

      if (redirectToCheckout) {
        window.location.assign(`${root}checkout`);
        return;
      }

      const cartDrawer = document.querySelector("cart-drawer");

      if (
        cartDrawer &&
        result.sections &&
        typeof cartDrawer.renderContents === "function"
      ) {
        cartDrawer.renderContents(result);
        cartDrawer.classList.remove("is-empty");
      } else if (result.sections) {
        this.updateCartSection("cart-drawer", "#CartDrawer");

        this.updateCartSection("cart-icon-bubble", "#cart-icon-bubble");
      }

      const cartResponse = await fetch(`${root}cart.js`, {
        headers: {
          Accept: "application/json",
          "X-Requested-With": "XMLHttpRequest",
        },
      });

      const cart = cartResponse.ok ? await cartResponse.json() : null;

      requestAnimationFrame(() => {
        if (!cartDrawer) return;

        cartDrawer.classList.remove("is-empty");

        if (typeof cartDrawer.open === "function") {
          cartDrawer.open();
        } else {
          cartDrawer.classList.add("active");
          cartDrawer.setAttribute("open", "");
        }
      });

      const detail = {
        cart,
        itemCount: cart?.item_count,
        source: "el-product",
        addedItems: result.items || [],
        sections: result.sections || {},
      };

      document.dispatchEvent(
        new CustomEvent("cart:updated", {
          bubbles: true,
          detail,
        }),
      );

      document.dispatchEvent(
        new CustomEvent("cart:rerender", {
          bubbles: true,
          detail,
        }),
      );

      document.dispatchEvent(
        new CustomEvent("cartQuantityUpdated", {
          bubbles: true,
          detail,
        }),
      );
    } catch (error) {
      console.error("EL Product add to cart error:", error);

      document.dispatchEvent(
        new CustomEvent("cart:error", {
          bubbles: true,
          detail: {
            error,
            source: "el-product",
          },
        }),
      );
    } finally {
      button.classList.remove("is-loading");
      button.disabled = false;

      if (atcLabel) {
        atcLabel.textContent = originalLabel;
      } else {
        button.textContent = originalLabel;
      }

      this.updateAtc();
    }
  }
  updateCartSection(sectionId, selector) {
    const section = document.querySelector(selector);

    if (!section) return;

    const root = window.Shopify?.routes?.root || "/";

    fetch(`${root}?sections=${sectionId}`, {
      headers: {
        Accept: "application/json",
        "X-Requested-With": "XMLHttpRequest",
      },
    })
      .then((response) => {
        if (!response.ok) return null;

        return response.json();
      })
      .then((sections) => {
        const html = sections?.[sectionId];

        if (!html) return;

        const parser = new DOMParser();
        const documentHTML = parser.parseFromString(html, "text/html");

        const updatedSection = documentHTML.querySelector(selector);

        if (!updatedSection) return;

        section.innerHTML = updatedSection.innerHTML;
      })
      .catch(() => {});
  }
  updateDeliveryDates() {
    this.querySelectorAll("[data-el-delivery-date]").forEach((node) => {
      const days = Number(node.dataset.days || 0);
      const date = new Date();
      date.setHours(12, 0, 0, 0);
      date.setDate(date.getDate() + days);
      const month = new Intl.DateTimeFormat(
        document.documentElement.lang || "en-US",
        { month: "long" },
      ).format(date);
      const day = date.getDate();
      node.textContent = `${month} ${day}${this.ordinal(day)}`;
    });
  }
  ordinal(number) {
    const mod100 = number % 100;
    if (mod100 >= 11 && mod100 <= 13) return "th";
    if (number % 10 === 1) return "st";
    if (number % 10 === 2) return "nd";
    if (number % 10 === 3) return "rd";
    return "th";
  }
  formatMoney(cents) {
    const format = this.productData.moneyFormat || "${{amount}}";
    const token = format.match(/\{\{\s*(\w+)\s*\}\}/)?.[1] || "amount";
    let precision = 2;
    let thousands = ",";
    let decimal = ".";
    if (token.includes("no_decimals")) precision = 0;
    if (token.includes("comma_separator")) {
      thousands = ".";
      decimal = ",";
    }
    if (token.includes("space_separator")) thousands = " ";
    if (token.includes("apostrophe_separator")) thousands = "'";
    const value = Number(cents || 0) / 100;
    const parts = value.toFixed(precision).split(".");
    const whole = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    const amount = precision ? `${whole}${decimal}${parts[1]}` : whole;
    return format.replace(/\{\{\s*\w+\s*\}\}/, amount);
  }
}
if (!customElements.get("el-product-information"))
  customElements.define("el-product-information", ElProductInformation);
